import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from './AuthContext';
import { useAdminClient } from './AdminClientContext';

/**
 * ReportingModeContext provides reporting mode + enabled modules for the application.
 * 
 * Modules:
 * - "people_lab": HR-focused reporting
 * - "market_lab": Marketing research reporting
 * - "pharma_lab": Pharma/healthcare reporting
 * 
 * Same data, different report layers.
 */

const ReportingModeContext = createContext();

// Terminology mappings for each mode
export const TERMINOLOGY = {
  peoplelab: {
    response: "Response",
    negativeResponse: "Resistance",
    positiveResponse: "Support",
    variance: "Polarization",
    confidence: "Strength of feeling",
    segment: "Employee group",
    neutral: "Neutral",
    showMetrics: ["overall_sentiment", "resistance_pct", "confidence", "risk_flags"],
    avoidTerms: ["purchase", "customer", "conversion", "sales"],
  },
  marketlab: {
    response: "Intent",
    negativeResponse: "Negative",
    positiveResponse: "Positive",
    variance: "Variation",
    confidence: "Confidence",
    segment: "Segment",
    neutral: "Neutral",
    showMetrics: ["positive_intent", "confidence", "segment_opportunity", "comparison"],
    avoidTerms: ["risk", "resistance", "fairness", "attrition", "sentiment"],
  },
  pharmalab: {
    response: "Perception",
    negativeResponse: "Concern",
    positiveResponse: "Favorable",
    variance: "Variation",
    confidence: "Confidence",
    segment: "Patient Segment",
    neutral: "Neutral",
    showMetrics: ["efficacy_perception", "safety_perception", "trust", "adherence"],
    avoidTerms: ["purchase", "sales", "conversion", "marketing"],
  }
};

// View configurations for each mode
export const VIEW_CONFIG = {
  peoplelab: {
    defaultLanding: "overall_sentiment",
    views: [
      { id: "overall_sentiment", name: "Overall Sentiment", description: "Distribution of Support / Neutral / Resistance" },
      { id: "risk_segments", name: "Risk Segments", description: "Groups reacting worse than population average" },
      { id: "scenario_comparison", name: "Scenario Comparison", description: "Compare sentiment changes across simulations" },
      { id: "open_ended_insights", name: "Open-ended Insights", description: "Clustered themes from text responses" }
    ],
    highlightRule: "worse_than_average",
    colorScheme: {
      negative: "#F87171",
      neutral: "#94A3B8",
      positive: "#86EFAC",
    }
  },
  marketlab: {
    defaultLanding: "pulse",
    views: [
      { id: "pulse", name: "Market Pulse", description: "Overall positive intent and confidence snapshot" },
      { id: "segments", name: "Segment Analysis", description: "Performance comparison across audience segments" },
      { id: "opportunity", name: "Opportunity Map", description: "Identify high-value target segments" },
      { id: "comparison", name: "Concept Compare", description: "Compare performance across concepts or messages" }
    ],
    highlightRule: "high_opportunity",
    colorScheme: {
      negative: "#94A3B8",
      neutral: "#3B82F6",
      positive: "#22C55E",
    }
  },
  pharmalab: {
    defaultLanding: "clinical_overview",
    views: [
      { id: "clinical_overview", name: "Clinical Overview", description: "Treatment perception and clinical potential" },
      { id: "safety", name: "Safety Profile", description: "Safety concerns and risk assessment" },
      { id: "adoption", name: "Adoption Pathway", description: "Awareness to adoption funnel" },
      { id: "segments", name: "Patient Segments", description: "Segment-level clinical perception analysis" }
    ],
    highlightRule: "safety_concern",
    colorScheme: {
      negative: "#F87171",
      neutral: "#60A5FA",
      positive: "#34D399",
    }
  }
};

// All valid module IDs
export const ALL_MODULES = [
  { id: "people_lab", label: "PeopleLab", description: "HR simulations", reportingMode: "peoplelab" },
  { id: "market_lab", label: "MarketLab", description: "Marketing research", reportingMode: "marketlab" },
  { id: "pharma_lab", label: "PharmaLab", description: "Pharma / healthcare", reportingMode: "pharmalab" },
];

export function ReportingModeProvider({ children }) {
  const [reportingMode, setReportingMode] = useState("peoplelab");
  const [enabledModules, setEnabledModules] = useState(["people_lab"]);
  const [clientInfo, setClientInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const { user, loading: authLoading } = useAuth();
  const { activeClient, selectedClientId } = useAdminClient();

  const fetchClientInfo = useCallback(async () => {
    if (authLoading) return;
    if (!user) { setLoading(false); return; }
    
    try {
      const response = await api.get('/auth/client-info');
      const client = response.data.client;
      setClientInfo(client);
      
      if (!activeClient && client) {
        if (client.reporting_mode) setReportingMode(client.reporting_mode);
        if (client.enabled_modules?.length) {
          setEnabledModules(client.enabled_modules);
        } else if (client.reporting_mode) {
          // Backward compat: derive from reporting_mode
          setEnabledModules(client.reporting_mode === "marketlab" ? ["market_lab"] : ["people_lab"]);
        }
      }
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error('Failed to fetch client info:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [user, authLoading, activeClient]);

  useEffect(() => { fetchClientInfo(); }, [fetchClientInfo]);
  
  useEffect(() => {
    if (activeClient) {
      if (activeClient.reporting_mode) setReportingMode(activeClient.reporting_mode);
      if (activeClient.enabled_modules?.length) {
        setEnabledModules(activeClient.enabled_modules);
      } else if (activeClient.reporting_mode) {
        setEnabledModules(activeClient.reporting_mode === "marketlab" ? ["market_lab"] : ["people_lab"]);
      } else {
        setReportingMode("peoplelab");
        setEnabledModules(["people_lab"]);
      }
    } else if (!selectedClientId && clientInfo) {
      if (clientInfo.reporting_mode) setReportingMode(clientInfo.reporting_mode);
      if (clientInfo.enabled_modules?.length) {
        setEnabledModules(clientInfo.enabled_modules);
      }
    }
  }, [activeClient, selectedClientId, clientInfo]);

  const getTerminology = (key) => TERMINOLOGY[reportingMode]?.[key] || key;
  const getViewConfig = () => VIEW_CONFIG[reportingMode];
  const shouldShowMetric = (metric) => TERMINOLOGY[reportingMode]?.showMetrics?.includes(metric);
  const shouldAvoidTerm = (term) => TERMINOLOGY[reportingMode]?.avoidTerms?.includes(term.toLowerCase());
  const getColorScheme = () => VIEW_CONFIG[reportingMode]?.colorScheme;

  const updateReportingMode = async (newMode) => {
    if (["peoplelab", "marketlab", "pharmalab"].includes(newMode)) {
      setReportingMode(newMode);
    }
  };

  const isModuleEnabled = (moduleId) => enabledModules.includes(moduleId);

  const value = {
    reportingMode,
    setReportingMode: updateReportingMode,
    enabledModules,
    setEnabledModules,
    clientInfo,
    loading,
    getTerminology,
    getViewConfig,
    shouldShowMetric,
    shouldAvoidTerm,
    getColorScheme,
    isModuleEnabled,
    isPeopleLab: enabledModules.includes("people_lab"),
    isMarketLab: enabledModules.includes("market_lab"),
    isPharmaLab: enabledModules.includes("pharma_lab"),
    refreshClientInfo: fetchClientInfo,
  };

  return (
    <ReportingModeContext.Provider value={value}>
      {children}
    </ReportingModeContext.Provider>
  );
}

export function useReportingMode() {
  const context = useContext(ReportingModeContext);
  if (!context) {
    throw new Error('useReportingMode must be used within a ReportingModeProvider');
  }
  return context;
}

export default ReportingModeContext;
